import type { Story } from '@src/types.ts/story';

/**
 * PLAT-11697 — whether a Content Studio surface should re-kick
 * `videoCreator.loadTranscriptIfNeeded()`.
 *
 * `TranscriptionStore.loadTranscription()` marks ANY error (a transient
 * `requestMissingFields` miss, a Dato hiccup) as `Failed`, and the only other
 * kick — ContentStudio's per-story effect — never re-fires. On the Sidecar
 * Path every transcription URL is already on the story at open, so the
 * ContentView mid-pending poll's missing-URL branch never re-kicked either and
 * the Transcript & Speakers / Captions tabs sat on "Preparing transcript
 * data…" until a Creator Studio round trip. Re-kick whenever the transcript
 * exists server-side but the store neither has it nor is fetching it —
 * `loadTranscriptIfNeeded()` is a no-op when ready and waits when loading.
 */
export interface TranscriptRekickInputs {
  /** The story carries a transcript to fetch (`transcription.elementsJson.url`). */
  hasElementsUrl: boolean;
  /** `TranscriptionStore.isTranscriptionReady` */
  isReady: boolean;
  /** `TranscriptionStore.isTranscriptionLoading` */
  isLoading: boolean;
}

export function shouldRekickTranscriptLoad({
  hasElementsUrl,
  isReady,
  isLoading,
}: TranscriptRekickInputs): boolean {
  return hasElementsUrl && !isReady && !isLoading;
}

/**
 * Whether the story carries a transcript to fetch: its elements JSON is on the
 * story, or the pipeline reports the transcript combine step done.
 */
export function storyHasTranscript(
  story: Pick<Story, 'transcription' | 'uploadLog'> | null | undefined,
): boolean {
  return (
    !!story?.transcription?.elementsJson?.url ||
    story?.uploadLog?.combineResults?.status === 'success'
  );
}
